/**
 * SupplierCatalogPage - Visualizzazione listino fornitore per buyer
 * 
 * Permette ai buyer di navigare il catalogo/listino di un fornitore attivo
 * senza necessariamente creare un ordine.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ArrowLeft,
  Search,
  Building2,
  Package,
  Euro,
  ShoppingCart,
  Filter,
  Grid3X3,
  List,
  Loader2,
  AlertCircle,
  Tag
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Product {
  id: string;
  name: string;
  sku?: string;
  category?: string;
  unit: string;
  price: number;
  description?: string;
  image_url?: string;
  is_available: boolean;
  min_order_qty?: number;
}

interface Supplier {
  id: string;
  name: string;
  type: string;
  logo_url?: string;
}

const SupplierCatalogPage = () => {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filtri
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    if (supplierId) {
      fetchCatalog();
    }
  }, [supplierId]);

  const fetchCatalog = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch supplier info and products
      const [supplierRes, productsRes] = await Promise.all([
        axios.get(`${API}/directory/${supplierId}`),
        axios.get(`${API}/products/catalog/${supplierId}`)
      ]);
      
      setSupplier(supplierRes.data);
      setProducts(productsRes.data.products || []);
      
      // Estrai categorie uniche
      const uniqueCategories = [...new Set(
        (productsRes.data.products || [])
          .map((p: Product) => p.category)
          .filter(Boolean)
      )] as string[];
      setCategories(uniqueCategories);
      
    } catch (err: any) {
      console.error('Error fetching catalog:', err);
      if (err.response?.status === 403) {
        setError('Non hai accesso a questo catalogo. Richiedi prima l\'accesso al fornitore.');
      } else if (err.response?.status === 404) {
        setError('Fornitore non trovato.');
      } else {
        setError('Errore nel caricamento del catalogo.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Filtra prodotti
  const filteredProducts = products.filter(product => {
    const matchesSearch = !search || 
      product.name.toLowerCase().includes(search.toLowerCase()) ||
      product.sku?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Raggruppa per categoria
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const cat = product.category || 'Altro';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {} as Record<string, Product[]>);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(price);
  };

  // Controllo accesso
  if (user?.role !== 'buyer') {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">Solo i buyer possono visualizzare i cataloghi</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 mx-auto text-destructive/50 mb-4" />
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna Indietro
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="supplier-catalog-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            {supplier?.logo_url ? (
              <img 
                src={supplier.logo_url} 
                alt={supplier.name}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-heading font-bold">{supplier?.name}</h1>
              <p className="text-sm text-muted-foreground capitalize">
                {supplier?.type} • {products.length} prodotti
              </p>
            </div>
          </div>
        </div>
        
        <Button onClick={() => navigate(`/orders/new?seller=${supplierId}`)}>
          <ShoppingCart className="h-4 w-4 mr-2" />
          Crea Ordine
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca prodotto per nome o codice..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="catalog-search"
          />
        </div>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex border rounded-lg">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            className="rounded-r-none"
            onClick={() => setViewMode('grid')}
          >
            <Grid3X3 className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            className="rounded-l-none"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-sm text-muted-foreground">
        <span>{filteredProducts.length} prodotti trovati</span>
        {categoryFilter !== 'all' && (
          <Badge variant="secondary" className="gap-1">
            <Tag className="h-3 w-3" />
            {categoryFilter}
            <button 
              onClick={() => setCategoryFilter('all')}
              className="ml-1 hover:text-foreground"
            >
              ×
            </button>
          </Badge>
        )}
      </div>

      {/* Products */}
      {filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Package className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              {search ? 'Nessun prodotto corrisponde alla ricerca' : 'Nessun prodotto disponibile'}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        // Grid View - Grouped by Category
        <div className="space-y-8">
          {Object.entries(groupedProducts).map(([category, categoryProducts]) => (
            <div key={category}>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Tag className="h-4 w-4 text-primary" />
                {category}
                <Badge variant="secondary" className="ml-2">{categoryProducts.length}</Badge>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {categoryProducts.map(product => (
                  <Card 
                    key={product.id} 
                    className={`transition-all hover:shadow-md ${!product.is_available ? 'opacity-60' : ''}`}
                    data-testid={`product-card-${product.id}`}
                  >
                    <CardContent className="p-4">
                      {product.image_url ? (
                        <div className="aspect-square rounded-lg overflow-hidden mb-3 bg-muted">
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="aspect-square rounded-lg bg-muted flex items-center justify-center mb-3">
                          <Package className="h-12 w-12 text-muted-foreground/30" />
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <div>
                          <h3 className="font-medium text-sm line-clamp-2">{product.name}</h3>
                          {product.sku && (
                            <p className="text-xs text-muted-foreground">COD: {product.sku}</p>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-bold text-primary">
                              {formatPrice(product.price)}
                            </p>
                            <p className="text-xs text-muted-foreground">/{product.unit}</p>
                          </div>
                          {!product.is_available && (
                            <Badge variant="secondary" className="text-xs">
                              Non disponibile
                            </Badge>
                          )}
                        </div>
                        
                        {product.min_order_qty && product.min_order_qty > 1 && (
                          <p className="text-xs text-muted-foreground">
                            Min. ordine: {product.min_order_qty} {product.unit}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // List View
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {filteredProducts.map(product => (
                <div 
                  key={product.id}
                  className={`flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors ${!product.is_available ? 'opacity-60' : ''}`}
                  data-testid={`product-row-${product.id}`}
                >
                  {product.image_url ? (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                      <Package className="h-8 w-8 text-muted-foreground/30" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{product.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {product.sku && <span>COD: {product.sku}</span>}
                      {product.category && (
                        <Badge variant="outline" className="text-xs">{product.category}</Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">
                      {formatPrice(product.price)}
                    </p>
                    <p className="text-xs text-muted-foreground">/{product.unit}</p>
                  </div>
                  
                  {!product.is_available && (
                    <Badge variant="secondary">Non disponibile</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SupplierCatalogPage;
